import type {
  Workspace,
  WorkspaceMember,
  WorkspaceRole,
} from '@/lib/api/workspaces.js';

/**
 * Derived permission set for the current user in a workspace. Gated on the shell role (the member's
 * role for the current user), mirroring `architecture.md` §4 — the UI decides up front what actions
 * to show or disable rather than letting a mutation 403 at the boundary.
 */
export interface WorkspaceCan {
  /**
   * Edit the workspace's General section (name/description/icon/color/privacy/status) and the Agent
   * section (agent instructions). Owner or editor.
   */
  manageSettings: boolean;
  /**
   * Invite a new member (editor/viewer only — `owner` is not grantable). Owner or editor; matches the
   * members `POST` gate on the shell.
   */
  addMembers: boolean;
  /** Change roles / flags / remove members. Owner-only on the current backend. */
  manageMembers: boolean;
  /** Archive or delete the workspace. Owner-only. */
  deleteWorkspace: boolean;
  /** Connect/disconnect connectors. Owner or editor. */
  manageConnectors: boolean;
}

/**
 * Resolve the current user's role in a workspace. Prefers the explicit membership row (from the
 * directory); falls back to `owner` when the caller created the workspace (the owner always has a
 * member row, but the directory can lag a fresh provision) and to `null` (no access) otherwise.
 * A platform admin is treated as `owner` so admin chrome is never gated off.
 */
export function resolveCurrentUserRole(args: {
  workspace: Workspace | undefined;
  members: WorkspaceMember[] | undefined;
  currentUserId: string | null;
  isAdmin: boolean;
}): WorkspaceRole | null {
  if (args.isAdmin) return 'owner';
  if (!args.currentUserId) return null;
  const member = args.members?.find((m) => m.userId === args.currentUserId);
  if (member) return member.role;
  if (args.workspace && args.workspace.createdBy === args.currentUserId)
    return 'owner';
  return null;
}

/** Derive the `can_*` permission set from a role. `null` (not a member) can do nothing. */
export function deriveCan(role: WorkspaceRole | null): WorkspaceCan {
  return {
    manageSettings: role === 'owner' || role === 'editor',
    addMembers: role === 'owner' || role === 'editor',
    manageMembers: role === 'owner',
    deleteWorkspace: role === 'owner',
    manageConnectors: role === 'owner' || role === 'editor',
  };
}

/**
 * Pick the workspace the switcher should select. Global-workspaces phase 2 removes the "All
 * workspaces" sentinel — the selection is always a concrete workspace. Rules, in order:
 *
 * 1. Keep the current selection when it still points at a known workspace.
 * 2. Otherwise select the user's personal workspace (`kind === 'personal'`, the auto-provisioned one).
 * 3. Otherwise the first active workspace, then the first of any kind.
 * 4. `null` only when the user has no workspaces at all (shouldn't happen once provisioning ran).
 */
export function resolveDefaultWorkspaceId(
  workspaces: Workspace[],
  selectedWorkspaceId: string | null,
): string | null {
  if (workspaces.length === 0) return null;
  if (
    selectedWorkspaceId !== null &&
    workspaces.some((workspace) => workspace.id === selectedWorkspaceId)
  ) {
    return selectedWorkspaceId;
  }
  const personal = workspaces.find(
    (workspace) => workspace.kind === 'personal',
  );
  if (personal) return personal.id;
  const firstActive = workspaces.find(
    (workspace) => workspace.status === 'active',
  );
  return (firstActive ?? workspaces[0]!).id;
}

/**
 * A deep-link (`?conversationId=`, `?file=`) can open an item of any kind — a chat, a file — that belongs
 * to a different workspace than the one currently selected. The main pane fetches it by id, but the
 * workspace-scoped sidebar wouldn't list it, so the two panes disagree. Return true when the opened
 * item's own workspace should be adopted as the selection so both panes agree: only when the item
 * carries a concrete workspace that differs from the current selection. A null item workspace (a legacy
 * pre-workspaces chat) leaves the selection untouched; a match is a no-op.
 */
export function shouldAdoptWorkspace(
  itemWorkspaceId: string | null,
  selectedWorkspaceId: string | null,
): boolean {
  return itemWorkspaceId !== null && itemWorkspaceId !== selectedWorkspaceId;
}

/**
 * Whether a workspace has anyone to share a file with — and the third value that says we do not know
 * yet, because the workspace row is a separate read that lags the surfaces gating on it.
 *
 * It is a third state rather than a default `false` because the two questions hanging off it fail in
 * opposite directions: {@link warnsAboutSharing} and {@link offersSharingControls}. One value deciding
 * both is what put sharing controls in a personal workspace's upload window.
 */
export type WorkspaceSharing = 'personal' | 'shared' | 'unknown';

/** Whether the workspace is personal, or null while its sharing state has not arrived. */
export function knownPersonal(sharing: WorkspaceSharing): boolean | null {
  return sharing === 'unknown' ? null : sharing === 'personal';
}

/** The sharing state of the workspace row, or `unknown` while it has not arrived. */
export function resolveWorkspaceSharing(
  workspace: Pick<Workspace, 'kind'> | undefined,
): WorkspaceSharing {
  if (!workspace) return 'unknown';
  return workspace.kind === 'personal' ? 'personal' : 'shared';
}

/**
 * Whether a confirmation must say that the action opens the file to other members. Fail-safe: it warns
 * until the workspace is known to be personal, because a warning nobody needed costs one sentence and a
 * missing one costs the file's privacy.
 */
export function warnsAboutSharing(sharing: WorkspaceSharing): boolean {
  return sharing !== 'personal';
}

/**
 * Whether to render the controls that open something to the OTHER MEMBERS — a file's visibility levels, a
 * chat's audience, the bulk Share / Make private. Fail-closed: they appear only in a workspace known to be
 * shared. There is nobody to open anything to in a personal workspace, and a control that is still there
 * because a read never finished is one the service answers 403 to.
 *
 * It does NOT decide whether a thing can be shared at all. A link or a copy leaving the workspace is a
 * different act, valid in a personal workspace too, and gated on the caller's right to the file or chat.
 */
export function offersSharingControls(sharing: WorkspaceSharing): boolean {
  return sharing === 'shared';
}

/** True when the workspace id appears in the caller's directory (member or owner). */
export function isAccessibleWorkspaceId(
  workspaceId: string,
  workspaces: readonly Workspace[],
): boolean {
  return workspaces.some((workspace) => workspace.id === workspaceId);
}
